import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { TextStyle } from '@tiptap/extension-text-style';
import { TextAlign } from '@tiptap/extension-text-align';
import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import Mustache from 'mustache';
import { useNavigate } from 'react-router-dom';
import { brandIdentity } from '../theme/brandIdentity';
import { fetchAuthSession } from 'aws-amplify/auth';
import Logo from './Logo';

// Variable catalog from original component
const VARIABLE_CATALOG = {
  'Cliente (Customer)': [
    { label: 'Cédula / ID', token: 'customer.id' },
    { label: 'Nombre completo', token: 'customer.full_name' },
    { label: 'Teléfono', token: 'customer.phone' },
    { label: 'Email', token: 'customer.email' },
    { label: 'Dirección', token: 'customer.address' },
    { label: 'Ciudad', token: 'customer.city' },
    { label: 'Firma (imagen)', token: 'customer.signature_image' }
  ],
  'Transacción (Transaction)': [
    { label: 'Número de transacción', token: 'transaction.id' },
    { label: 'Fecha (YYYY-MM-DD)', token: 'transaction.date' },
    { label: 'Hora (HH:mm)', token: 'transaction.time' }
  ],
  'Dispositivo (Device)': [
    { label: 'Marca', token: 'device.brand' },
    { label: 'Nombre/Modelo', token: 'device.name' },
    { label: 'Capacidad (GB)', token: 'device.storage' },
    { label: 'Color', token: 'device.color' },
    { label: 'IMEI 1', token: 'device.imei1' },
    { label: 'IMEI 2', token: 'device.imei2' },
    { label: 'IMEI 1 - Cód. Barras', token: 'device.imei1_barcode' },
    { label: 'IMEI 2 - Cód. Barras', token: 'device.imei2_barcode' },
    { label: 'Grado del dispositivo', token: 'device.grade' }
  ],
  'Comprador (trader)': [
    { label: 'Nombre legal', token: 'trader.name' },
    { label: 'NIT/RFC', token: 'trader.tax_id' },
    { label: 'Teléfono', token: 'trader.phone' },
    { label: 'Email', token: 'trader.email' },
    { label: 'Dirección', token: 'trader.address' },
    { label: 'Ciudad', token: 'trader.city' }
  ],
  'Tienda (Store)': [
    { label: 'Nombre de la tienda', token: 'store.name' },
    { label: 'Ciudad', token: 'store.city' }
  ],
  'Venta (Sold)': [
    { label: 'Dispositivo vendido', token: 'sold.device' },
    { label: 'Precio del dispositivo vendido', token: 'sold.price' }
  ],
  'Montos (Amounts)': [
    { label: 'Bono oferta comprador', token: 'amounts.bonus_trader' },
    { label: 'Bono adicional marca', token: 'amounts.bonus_brand' },
    { label: 'Total', token: 'amounts.total' }
  ],
  'Asesor (Advisor)': [
    { label: 'Nombre', token: 'advisor.name' },
    { label: 'ID', token: 'advisor.id' }
  ],
  'Códigos SKU (SKU Codes)': [
    { label: 'SKU Bono del comprador - Valor', token: 'codes.bonus_trader_value' },
    { label: 'SKU Bono de la marca - Valor', token: 'codes.bonus_brand_value' },
    { label: 'SKU Bono del comprador - Cód. Barras', token: 'codes.bonus_trader_barcode' },
    { label: 'SKU Bono de la marca - Cód. Barras', token: 'codes.bonus_brand_barcode' }
  ],
  'Firmas/Fotos (Signatures/Photos)': [
    { label: 'Imagen anverso doc.', token: 'photos.front_image' },
    { label: 'Imagen reverso doc.', token: 'photos.back_image' }
  ]
};

// Custom Page Break Node Extension
const PageBreak = Node.create({
  name: 'pageBreak',
  
  group: 'block',
  
  atom: true,
  
  selectable: true,
  
  parseHTML() {
    return [
      {
        tag: 'div[data-page-break]',
      },
    ];
  },
  
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        'data-page-break': 'true',
        'class': 'page-break',
        'contentEditable': false,
      },
      '📄 SALTO DE PÁGINA',
    ];
  },
  
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const div = document.createElement('div');
      div.setAttribute('data-page-break', 'true');
      div.className = 'page-break';
      div.contentEditable = false;
      div.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px;
          margin: 32px 0;
          border-top: 2px dashed #6366F1;
          border-bottom: 2px dashed #6366F1;
          background: #F0F9FF;
          color: #6366F1;
          font-weight: 600;
          font-size: 14px;
          border-radius: 4px;
          position: relative;
          user-select: none;
        ">
          <span>📄</span>
          <span>SALTO DE PÁGINA</span>
          <button 
            style="
              position: absolute;
              top: 8px;
              right: 8px;
              background: #DC2626;
              color: white;
              border: none;
              border-radius: 50%;
              width: 20px;
              height: 20px;
              cursor: pointer;
              font-size: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              opacity: 0.8;
            "
            onclick="this.closest('[data-page-break]').remove()"
            title="Eliminar salto de página"
          >×</button>
        </div>
      `;
      
      return {
        dom: div,
      };
    };
  },
  
  addCommands() {
    return {
      insertPageBreak: () => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
        });
      },
    };
  },
});

// Custom Variable Node Extension
const Variable = Node.create({
  name: 'variable',
  
  group: 'inline',
  
  inline: true,
  
  atom: true,
  
  addAttributes() {
    return {
      key: {
        default: null,
      },
      label: {
        default: null,
      },
    };
  },
  
  parseHTML() {
    return [
      {
        tag: 'span[data-variable]',
        getAttrs: dom => ({
          key: dom.getAttribute('data-variable'),
          label: dom.textContent.replace(/[{}]/g, ''),
        }),
      },
    ];
  },
  
  renderHTML({ HTMLAttributes, node }) {
    const { key, label } = node.attrs;
    return [
      'span',
      {
        'data-variable': key,
        'class': 'variable-chip',
        'contentEditable': false,
      },
      `{{${key}}}`,
    ];
  },
  
  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const span = document.createElement('span');
      span.setAttribute('data-variable', node.attrs.key);
      span.className = 'variable-chip';
      span.contentEditable = false;
      span.draggable = true;
      span.textContent = `{{${node.attrs.key}}}`;
      span.title = `Variable: ${node.attrs.key}`;
      
      return {
        dom: span,
      };
    };
  },
});

// Font Size Extension
const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace('px', ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}px`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run();
      },
    };
  },
});

// Suggestion Plugin for autocomplete
const suggestionPluginKey = new PluginKey('suggestion');

const SuggestionPlugin = (options) => {
  return new Plugin({
    key: suggestionPluginKey,
    
    state: {
      init() {
        return {
          active: false,
          range: null,
          query: '',
        };
      },
      
      apply(tr, value, oldState, newState) {
        const { selection } = newState;
        const next = { ...value };
        
        if (selection.from !== selection.to) {
          next.active = false;
          next.range = null;
          next.query = '';
          return next;
        }
        
        const $position = selection.$from;
        const textBefore = $position.nodeBefore?.text || '';
        
        // Check for trigger characters (/ or @)
        const triggerMatch = textBefore.match(/[/@]([^/@ ]*)$/);
        
        if (triggerMatch && !triggerMatch[1].includes(' ')) {
          const query = triggerMatch[1];
          const range = {
            from: $position.pos - triggerMatch[0].length,
            to: $position.pos,
          };
          
          next.active = true;
          next.range = range;
          next.query = query;
          
          // Call the onUpdate callback
          if (options.onUpdate) {
            options.onUpdate(next);
          }
        } else {
          next.active = false;
          next.range = null;
          next.query = '';
          
          if (options.onUpdate) {
            options.onUpdate(next);
          }
        }
        
        return next;
      },
    },
    
    props: {
      decorations(state) {
        const { active, range } = this.getState(state);
        
        if (!active) return null;
        
        return DecorationSet.create(state.doc, [
          Decoration.inline(range.from, range.to, {
            class: 'suggestion-highlight',
          }),
        ]);
      },
    },
  });
};

const ContractEditor = () => {
  const navigate = useNavigate();
  const [contractName, setContractName] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionQuery, setSuggestionQuery] = useState('');
  const [suggestionRange, setSuggestionRange] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showTableSizeSelector, setShowTableSizeSelector] = useState(false);
  const [cursorInTable, setCursorInTable] = useState(false);
  const [importStatus, setImportStatus] = useState({ status: 'idle', message: '', fileName: '' });
  const [uploadStatus, setUploadStatus] = useState({ status: 'idle', message: '', contractId: '', version: '' });
  const [sampleData, setSampleData] = useState({
    customer: {
      id: '12345678',
      full_name: 'Juan Pérez García',
      phone: '+57 300 123 4567',
      email: 'juan.perez@email.com',
      address: 'Calle 123 #45-67',
      city: 'Bogotá',
      signature_image: '[Imagen de firma]'
    },
    transaction: {
      id: 'TXN-2024-001',
      date: '2024-01-15',
      time: '14:30'
    },
    device: {
      brand: 'Apple',
      name: 'iPhone 14 Pro',
      storage: '256',
      color: 'Azul Sierra',
      imei1: '123456789012345',
      imei2: '543210987654321',
      imei1_barcode: '[Código de barras IMEI1]',
      imei2_barcode: '[Código de barras IMEI2]',
      grade: 'A+'
    },
    trader: {
      name: 'Retoma Corp',
      tax_id: '900123456-7',
      phone: '+57 1 234 5678',
      email: 'info@retoma.com',
      address: 'Carrera 7 #123-45',
      city: 'Bogotá'
    },
    store: {
      name: 'Tienda Centro',
      city: 'Bogotá'
    },
    sold: {
      device: 'iPhone 13',
      price: '$2,500,000'
    },
    amounts: {
      bonus_trader: '$300,000',
      bonus_brand: '$200,000',
      total: '$500,000'
    },
    advisor: {
      name: 'María González',
      id: 'ADV001'
    },
    codes: {
      bonus_trader_value: 'SKU123',
      bonus_brand_value: 'SKU456',
      bonus_trader_barcode: '[Código SKU123]',
      bonus_brand_barcode: '[Código SKU456]'
    },
    photos: {
      front_image: '[Imagen frontal documento]',
      back_image: '[Imagen posterior documento]'
    }
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontSize,
      Variable,
      PageBreak,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      SuggestionPlugin({
        onUpdate: ({ active, query, range }) => {
          setShowSuggestions(active);
          setSuggestionQuery(query);
          setSuggestionRange(range);
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      // Check if cursor is inside a table and update state
      setCursorInTable(editor.isActive('table'));
      // Auto-save or validation logic can go here
    },
    onSelectionUpdate: ({ editor }) => {
      // Also check on selection changes
      setCursorInTable(editor.isActive('table'));
    },
  });

  const handleCancel = () => {
    if (window.confirm('¿Estás seguro de que quieres cancelar? Los cambios no guardados se perderán.')) {
      navigate('/dashboard');
    }
  };

  const getAllVariables = () => {
    const allVars = [];
    Object.values(VARIABLE_CATALOG).forEach(category => {
      category.forEach(variable => {
        allVars.push(variable);
      });
    });
    return allVars;
  };

  const getFilteredVariables = (query) => {
    if (!query) return getAllVariables();
    
    return getAllVariables().filter(variable =>
      variable.label.toLowerCase().includes(query.toLowerCase()) ||
      variable.token.toLowerCase().includes(query.toLowerCase())
    );
  };

  const insertVariable = (token) => {
    if (!editor || !suggestionRange) return;
    
    const label = getAllVariables().find(v => v.token === token)?.label || token;
    
    editor
      .chain()
      .focus()
      .deleteRange(suggestionRange)
      .insertContent({
        type: 'variable',
        attrs: { key: token, label },
      })
      .run();
    
    setShowSuggestions(false);
  };

  const insertVariableFromSidebar = (token) => {
    if (!editor) return;
    
    const label = getAllVariables().find(v => v.token === token)?.label || token;
    
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'variable',
        attrs: { key: token, label },
      })
      .run();
  };

  // Table manipulation functions
  const addTableRow = () => {
    if (!editor) return;
    editor.chain().focus().addRowAfter().run();
  };

  const deleteTableRow = () => {
    if (!editor) return;
    editor.chain().focus().deleteRow().run();
  };

  const addTableColumn = () => {
    if (!editor) return;
    editor.chain().focus().addColumnAfter().run();
  };

  const deleteTableColumn = () => {
    if (!editor) return;
    editor.chain().focus().deleteColumn().run();
  };

  const deleteTable = () => {
    if (!editor) return;
    if (window.confirm('¿Estás seguro de que quieres eliminar la tabla completa?')) {
      editor.chain().focus().deleteTable().run();
    }
  };

  const canManipulateTable = () => {
    return cursorInTable;
  };

  const setFontSize = (size) => {
    if (!editor) return;
    editor.chain().focus().setFontSize(size).run();
  };

  const insertPageBreak = () => {
    if (!editor) return;
    // Use insertContent directly as a fallback
    editor.chain().focus().insertContent({
      type: 'pageBreak'
    }).run();
  };


  const insertTable = (rows = 3, cols = 3, withHeader = true) => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: withHeader }).run();
    setShowTableSizeSelector(false);
  };

  const insertTableWithSize = () => {
    setShowTableSizeSelector(!showTableSizeSelector);
  };

  const TABLE_PRESETS = [
    { name: '2x2 Simple', rows: 2, cols: 2, header: false },
    { name: '3x2 Lista', rows: 3, cols: 2, header: true },
    { name: '3x3 Estándar', rows: 3, cols: 3, header: true },
    { name: '4x3 Amplia', rows: 4, cols: 3, header: true },
    { name: '5x2 Vertical', rows: 5, cols: 2, header: true },
    { name: '2x4 Horizontal', rows: 2, cols: 4, header: true },
    { name: '3x4 Grande', rows: 3, cols: 4, header: true },
    { name: '5x5 Máxima', rows: 5, cols: 5, header: true },
  ];

  const exportDocument = () => {
    if (!editor || !contractName.trim()) {
      alert('Por favor ingresa un nombre para el contrato');
      return;
    }

    const json = editor.getJSON();
    const html = editor.getHTML();
    
    // Extract variables used in the document
    const usedVariables = [];
    const extractVariables = (node) => {
      if (node.type === 'variable' && node.attrs?.key) {
        if (!usedVariables.includes(node.attrs.key)) {
          usedVariables.push(node.attrs.key);
        }
      }
      if (node.content) {
        node.content.forEach(extractVariables);
      }
    };
    
    json.content?.forEach(extractVariables);

    const exportData = {
      version: '3.0',
      editor: 'tiptap',
      contract: {
        id: `contract-${Date.now()}`,
        name: contractName.trim(),
        created_at: new Date().toISOString(),
        locale: 'es-CO'
      },
      content: {
        json: json,
        html: html,
        plain_text: editor.getText(),
        word_count: editor.getText().split(/\s+/).filter(word => word.length > 0).length
      },
      variables: {
        used: usedVariables,
        catalog: VARIABLE_CATALOG,
        sample_data: sampleData
      },
      metadata: {
        editor_version: '3.0-tiptap',
        format_version: '1.0',
        last_modified: new Date().toISOString(),
        features: {
          has_tables: html.includes('<table'),
          has_variables: usedVariables.length > 0,
          extensible: true
        }
      }
    };

    // Download JSON file
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const fileName = `contract-tiptap-${contractName.replace(/[^a-zA-Z0-9\s]/g, '_').replace(/\s+/g, '_')}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', fileName);
    linkElement.click();
    
    console.log('Documento exportado:', exportData);
    alert(`Contrato "${contractName}" exportado con Tiptap. El archivo incluye formato JSON nativo para máxima extensibilidad.`);
  };

  const generatePreview = () => {
    if (!editor) return '';
    
    let html = editor.getHTML();
    
    // Replace variables with sample data using Mustache
    try {
      // Convert Tiptap variables to Mustache format
      html = html.replace(/<span[^>]*data-variable="([^"]*)"[^>]*>{{([^}]*)}}<\/span>/g, '{{$1}}');
      
      // Render with Mustache
      html = Mustache.render(html, sampleData);
    } catch (error) {
      console.error('Error rendering preview:', error);
    }
    
    return html;
  };

  // JSON Import Functions
  const validateJSONStructure = (data) => {
    const errors = [];
    
    // Validate basic structure
    if (!data.content?.json?.type || data.content.json.type !== "doc") {
      errors.push("Falta content.json.type = 'doc'");
    }
    
    if (!Array.isArray(data.content?.json?.content)) {
      errors.push("content.json.content debe ser un array");
    }
    
    if (data.variables && typeof data.variables.sample_data !== "object") {
      errors.push("variables.sample_data debe ser un objeto");
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const sanitizeAndValidateNodes = (content) => {
    if (!Array.isArray(content)) return [];
    
    return content.map(node => {
      // Validate known node types
      switch (node.type) {
        case 'paragraph':
        case 'heading':
          return {
            ...node,
            content: node.content ? sanitizeAndValidateNodes(node.content) : []
          };
          
        case 'table':
          return {
            ...node,
            content: node.content ? node.content.map(row => ({
              ...row,
              type: 'tableRow',
              content: row.content ? row.content.map(cell => ({
                ...cell,
                type: cell.type === 'tableHeader' ? 'tableHeader' : 'tableCell',
                content: cell.content ? sanitizeAndValidateNodes(cell.content) : []
              })) : []
            })) : []
          };
          
        case 'variable':
          // Validate variable node has required attrs
          if (!node.attrs?.key || typeof node.attrs.key !== 'string') {
            console.warn('Variable node missing key, converting to text');
            return {
              type: 'text',
              text: `{{${node.attrs?.key || 'unknown'}}}`
            };
          }
          return node;
          
        case 'text':
          return node;
          
        case 'pageBreak':
          // Page break node
          return node;
          
        default:
          // Unknown node type, convert to paragraph
          console.warn(`Unknown node type: ${node.type}, converting to paragraph`);
          return {
            type: 'paragraph',
            content: node.content ? sanitizeAndValidateNodes(node.content) : [
              { type: 'text', text: `[${node.type}]` }
            ]
          };
      }
    });
  };

  const importContractFromJSON = async (file) => {
    if (!file) return;
    
    // Validate file size (5MB limit)
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_SIZE) {
      setImportStatus({
        status: 'error',
        message: `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo permitido: 5MB`,
        fileName: file.name
      });
      return;
    }

    setImportStatus({
      status: 'loading',
      message: 'Cargando archivo...',
      fileName: file.name
    });

    try {
      // Read file
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // Parse JSON
      let jsonData;
      try {
        jsonData = JSON.parse(fileContent);
      } catch (parseError) {
        throw new Error('Archivo JSON inválido: ' + parseError.message);
      }

      // Validate structure
      const validation = validateJSONStructure(jsonData);
      if (!validation.isValid) {
        throw new Error('Estructura del archivo incorrecta: ' + validation.errors.join(', '));
      }

      // Sanitize and validate nodes
      const sanitizedContent = {
        ...jsonData.content.json,
        content: sanitizeAndValidateNodes(jsonData.content.json.content)
      };

      // Update sample data if provided
      if (jsonData.variables?.sample_data) {
        setSampleData(prev => ({
          ...prev,
          ...jsonData.variables.sample_data
        }));
        console.log('📦 Sample data updated:', jsonData.variables.sample_data);
      } else {
        console.warn('⚠️ No sample_data found in imported file, using default');
      }

      // Load content into editor
      if (editor) {
        editor.commands.setContent(sanitizedContent, false);
        
        // Update contract name if provided
        if (jsonData.contract?.name) {
          setContractName(jsonData.contract.name);
        }
        
        setImportStatus({
          status: 'success',
          message: `Contrato importado exitosamente${jsonData.variables?.sample_data ? ' (con datos de muestra)' : ''}`,
          fileName: file.name
        });
        
        console.log('✅ Contract imported successfully:', {
          nodeCount: sanitizedContent.content?.length || 0,
          hasVariables: !!jsonData.variables?.sample_data,
          contractName: jsonData.contract?.name
        });
      }

    } catch (error) {
      console.error('❌ Import error:', error);
      setImportStatus({
        status: 'error',
        message: error.message || 'Error al importar el archivo',
        fileName: file.name
      });
    }
  };

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        importContractFromJSON(file);
      }
    };
    input.click();
  };

  const resetImportStatus = () => {
    setImportStatus({ status: 'idle', message: '', fileName: '' });
  };

  const resetUploadStatus = () => {
    setUploadStatus({ status: 'idle', message: '', contractId: '', version: '' });
  };

  const uploadContractToDatabase = async () => {
    if (!editor || !contractName.trim()) {
      alert('Por favor ingresa un nombre para el contrato antes de subirlo a la base de datos');
      return;
    }

    setUploadStatus({
      status: 'loading',
      message: 'Subiendo contrato a la base de datos...',
      contractId: '',
      version: ''
    });

    try {
      // Obtener el token de autenticación de Cognito
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el token de autenticación');
      }

      const json = editor.getJSON();
      
      // Preparar el payload según el ejemplo proporcionado
      const payload = {
        name: contractName.trim(),
        locale: "es-CO",
        content_json: {
          content: {
            json: json
          },
          variables: { 
            sample_data: sampleData 
          }
        }
      };

      console.log('📤 Enviando contrato a la API:', payload);

      // Hacer la petición POST a la API
      const response = await fetch('https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/contracts_templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(payload)
      });

      console.log('📡 Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error del servidor:', errorText);
        throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Contrato subido exitosamente:', result);

      setUploadStatus({
        status: 'success',
        message: result.message || 'Contrato creado exitosamente',
        contractId: result.contract_id || '',
        version: result.version || ''
      });

    } catch (error) {
      console.error('❌ Error al subir contrato:', error);
      setUploadStatus({
        status: 'error',
        message: error.message || 'Error al subir el contrato a la base de datos',
        contractId: '',
        version: ''
      });
    }
  };

  // Close table size selector when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTableSizeSelector && !event.target.closest('.table-size-container')) {
        setShowTableSizeSelector(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTableSizeSelector]);

  // Auto-clear success/error status after 5 seconds
  React.useEffect(() => {
    if (importStatus.status === 'success' || importStatus.status === 'error') {
      const timer = setTimeout(() => {
        resetImportStatus();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [importStatus.status]);

  // Auto-clear upload status after 5 seconds
  React.useEffect(() => {
    if (uploadStatus.status === 'success' || uploadStatus.status === 'error') {
      const timer = setTimeout(() => {
        resetUploadStatus();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [uploadStatus.status]);

  if (!editor) {
    return <div>Cargando editor...</div>;
  }

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <Logo variant="horizontal" size="medium" />
        <div className="header-title">Plantillas de Contratos</div>
        <div className="user-menu">
          <button 
            onClick={() => setPreviewMode(!previewMode)}
            className="btn btn-outline"
            style={{ marginRight: brandIdentity.spacing.sm }}
          >
            {previewMode ? 'Editar' : 'Vista Previa'}
          </button>
          <button 
            onClick={handleCancel}
            className="btn btn-outline"
            style={{ marginRight: brandIdentity.spacing.sm }}
          >
            Cancelar
          </button>
          <button 
            onClick={handleImportClick}
            className="btn btn-outline"
            style={{ marginRight: brandIdentity.spacing.sm }}
          >
            📁 Importar JSON
          </button>
          <button
            onClick={exportDocument}
            disabled={!contractName.trim()}
            className="btn btn-outline"
            style={{ marginRight: brandIdentity.spacing.sm }}
          >
            💾 Exportar JSON
          </button>
          <button
            onClick={uploadContractToDatabase}
            disabled={!contractName.trim() || uploadStatus.status === 'loading'}
            className="btn"
            style={{
              backgroundColor: contractName.trim() && uploadStatus.status !== 'loading' 
                ? brandIdentity.colors.primary 
                : brandIdentity.colors.textLight,
              cursor: contractName.trim() && uploadStatus.status !== 'loading' 
                ? 'pointer' 
                : 'not-allowed',
              opacity: uploadStatus.status === 'loading' ? 0.7 : 1
            }}
          >
            {uploadStatus.status === 'loading' ? '⏳ Subiendo...' : '☁️ Subir a Base de Datos'}
          </button>
        </div>
      </header>
      
      {/* Main Container */}
      <div className="container" style={{ position: 'relative' }}>
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Nueva Plantilla de Contrato</h2>
            <p style={{ 
              margin: 0, 
              color: brandIdentity.colors.textLight,
              fontSize: brandIdentity.typography.sizes.small 
            }}>
              Crea plantillas de contratos con editor avanzado y variables dinámicas
            </p>
            
            {/* Import Status */}
            {importStatus.status !== 'idle' && (
              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '6px',
                border: '1px solid',
                backgroundColor: 
                  importStatus.status === 'loading' ? '#FEF3C7' :
                  importStatus.status === 'success' ? '#D1FAE5' :
                  '#FEE2E2',
                borderColor:
                  importStatus.status === 'loading' ? '#F59E0B' :
                  importStatus.status === 'success' ? '#10B981' :
                  '#EF4444',
                color:
                  importStatus.status === 'loading' ? '#92400E' :
                  importStatus.status === 'success' ? '#047857' :
                  '#B91C1C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>
                    {importStatus.status === 'loading' ? '⏳' :
                     importStatus.status === 'success' ? '✅' :
                     '❌'}
                  </span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      {importStatus.fileName}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      {importStatus.message}
                    </div>
                  </div>
                </div>
                {importStatus.status !== 'loading' && (
                  <button
                    onClick={resetImportStatus}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px'
                    }}
                    title="Cerrar"
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            {/* Upload Status */}
            {uploadStatus.status !== 'idle' && (
              <div style={{
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '6px',
                border: '1px solid',
                backgroundColor: 
                  uploadStatus.status === 'loading' ? '#FEF3C7' :
                  uploadStatus.status === 'success' ? '#D1FAE5' :
                  '#FEE2E2',
                borderColor:
                  uploadStatus.status === 'loading' ? '#F59E0B' :
                  uploadStatus.status === 'success' ? '#10B981' :
                  '#EF4444',
                color:
                  uploadStatus.status === 'loading' ? '#92400E' :
                  uploadStatus.status === 'success' ? '#047857' :
                  '#B91C1C',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>
                    {uploadStatus.status === 'loading' ? '⏳' :
                     uploadStatus.status === 'success' ? '✅' :
                     '❌'}
                  </span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>
                      {uploadStatus.status === 'success' && uploadStatus.contractId 
                        ? `Contrato ID: ${uploadStatus.contractId} (v${uploadStatus.version})`
                        : 'Subida a Base de Datos'
                      }
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      {uploadStatus.message}
                    </div>
                  </div>
                </div>
                {uploadStatus.status !== 'loading' && (
                  <button
                    onClick={resetUploadStatus}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px'
                    }}
                    title="Cerrar"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Contract Name */}
          <div className="form-group">
            <label className="form-label">
              Nombre del contrato
            </label>
            <input
              type="text"
              value={contractName}
              onChange={(e) => setContractName(e.target.value)}
              placeholder="Ingresa el nombre del contrato..."
              className="form-input"
            />
          </div>

          {/* Editor Section */}
          <div className="editor-section">
            <h3 className="section-title">
              Editor Tiptap {previewMode && '- Vista Previa con Datos de Ejemplo'}
            </h3>
            
            {!previewMode && (
              /* Toolbar */
              <div className="editor-toolbar">
                {/* Text Formatting */}
                <button 
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                  title="Negrita"
                >
                  <strong>B</strong>
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                  title="Cursiva"
                >
                  <em>I</em>
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
                  title="Subrayado"
                >
                  <u>U</u>
                </button>
                
                <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                
                {/* Alignment */}
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`}
                  title="Alinear izquierda"
                >
                  ⬅
                </button>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`}
                  title="Centrar"
                >
                  ⬄
                </button>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`}
                  title="Alinear derecha"
                >
                  ➡
                </button>
                <button 
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                  className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`}
                  title="Justificar"
                >
                  ⬌
                </button>
                
                <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                
                {/* Lists */}
                <button 
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                  title="Lista con viñetas"
                >
                  •
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                  title="Lista numerada"
                >
                  1.
                </button>
                
                <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                
                {/* Headings */}
                <button 
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
                  title="Título 1"
                >
                  H1
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                  title="Título 2"
                >
                  H2
                </button>
                <button 
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
                  title="Título 3"
                >
                  H3
                </button>
                
                <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                
                {/* Table */}
                <div className="table-size-container" style={{ position: 'relative', display: 'inline-block' }}>
                  <button 
                    onClick={insertTableWithSize}
                    className="toolbar-btn"
                    title="Insertar tabla (seleccionar tamaño)"
                  >
                    ⚏
                  </button>
                  
                  {showTableSizeSelector && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      backgroundColor: 'white',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      zIndex: 1000,
                      minWidth: '200px',
                      marginTop: '4px'
                    }}>
                      <div style={{ 
                        padding: '8px 12px', 
                        borderBottom: '1px solid #E5E7EB', 
                        fontSize: '12px', 
                        fontWeight: '600',
                        color: '#374151'
                      }}>
                        Seleccionar tamaño de tabla
                      </div>
                      {TABLE_PRESETS.map((preset, index) => (
                        <div
                          key={index}
                          onClick={() => insertTable(preset.rows, preset.cols, preset.header)}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#374151',
                            borderBottom: index < TABLE_PRESETS.length - 1 ? '1px solid #F3F4F6' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#F3F4F6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>{preset.name}</span>
                          <span style={{ fontSize: '11px', color: '#6B7280' }}>
                            {preset.rows}×{preset.cols} {preset.header ? '+ header' : ''}
                          </span>
                        </div>
                      ))}
                      <div style={{ 
                        padding: '8px 12px', 
                        borderTop: '1px solid #E5E7EB',
                        fontSize: '11px',
                        color: '#6B7280',
                        textAlign: 'center'
                      }}>
                        Puedes agregar/quitar filas y columnas después
                      </div>
                    </div>
                  )}
                </div>
                
                <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                
                {/* Page Break */}
                <button 
                  onClick={insertPageBreak}
                  className="toolbar-btn"
                  title="Insertar salto de página"
                >
                  📄
                </button>
                
                {/* Table Controls - Only show when cursor is inside a table */}
                {canManipulateTable() && (
                  <>
                    <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                    
                    {/* Table manipulation buttons */}
                    <button 
                      onClick={addTableRow}
                      className="toolbar-btn table-control"
                      title="Agregar fila después"
                    >
                      +📋
                    </button>
                    <button 
                      onClick={deleteTableRow}
                      className="toolbar-btn table-control"
                      title="Eliminar fila actual"
                    >
                      -📋
                    </button>
                    <button 
                      onClick={addTableColumn}
                      className="toolbar-btn table-control"
                      title="Agregar columna después"
                    >
                      +📄
                    </button>
                    <button 
                      onClick={deleteTableColumn}
                      className="toolbar-btn table-control"
                      title="Eliminar columna actual"
                    >
                      -📄
                    </button>
                    <button 
                      onClick={deleteTable}
                      className="toolbar-btn table-control"
                      style={{ 
                        backgroundColor: '#FEE2E2',
                        color: '#DC2626',
                        borderColor: '#FECACA'
                      }}
                      title="Eliminar tabla completa"
                    >
                      🗑️
                    </button>
                  </>
                )}
                
                <div style={{ width: '1px', height: '24px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
                
                {/* Font Size */}
                <select
                  onChange={(e) => setFontSize(e.target.value)}
                  className="toolbar-btn"
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '100px'
                  }}
                  title="Tamaño de fuente"
                >
                  <option value="">Tamaño</option>
                  <option value="12">12px</option>
                  <option value="14">14px</option>
                  <option value="16">16px</option>
                  <option value="18">18px</option>
                  <option value="20">20px</option>
                  <option value="24">24px</option>
                </select>
              </div>
            )}
            
            {/* Editor Content */}
            <div style={{ position: 'relative' }}>
              {previewMode ? (
                <div 
                  className="contract-editor preview-mode"
                  dangerouslySetInnerHTML={{ __html: generatePreview() }}
                />
              ) : (
                <EditorContent 
                  editor={editor}
                  className="contract-editor"
                />
              )}
              
              {/* Autocomplete Suggestions */}
              {showSuggestions && !previewMode && (
                <div className="autocomplete-menu" style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  backgroundColor: brandIdentity.colors.white,
                  border: `1px solid ${brandIdentity.colors.borderLight}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  zIndex: 1500,
                  width: '280px',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <div style={{ 
                    padding: '8px 12px', 
                    borderBottom: `1px solid ${brandIdentity.colors.borderLight}`, 
                    fontSize: brandIdentity.typography.sizes.small, 
                    color: brandIdentity.colors.textLight 
                  }}>
                    Variables disponibles {suggestionQuery && `para "${suggestionQuery}"`}
                  </div>
                  {getFilteredVariables(suggestionQuery).slice(0, 10).map((variable) => (
                    <div
                      key={variable.token}
                      onClick={() => insertVariable(variable.token)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: brandIdentity.typography.sizes.small,
                        color: brandIdentity.colors.textDark,
                        borderBottom: `1px solid ${brandIdentity.colors.borderLight}`
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = brandIdentity.colors.backgroundLight;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      {variable.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Variables Sidebar */}
          {!previewMode && (
            <div style={{
              position: 'absolute',
              right: '-320px',
              top: '0',
              height: 'auto',
              width: '300px',
              backgroundColor: brandIdentity.colors.white,
              border: `1px solid ${brandIdentity.colors.borderLight}`,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              overflowY: 'auto',
              padding: '20px',
              maxHeight: '600px'
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: brandIdentity.typography.sizes.large,
                fontWeight: brandIdentity.typography.weights.bold,
                color: brandIdentity.colors.textDark
              }}>
                Variables Disponibles
              </h3>
              <p style={{
                margin: '0 0 20px 0',
                fontSize: brandIdentity.typography.sizes.small,
                color: brandIdentity.colors.textLight
              }}>
                Haz clic para insertar campos. También puedes usar / o @ seguido del nombre.
              </p>
              
              {Object.entries(VARIABLE_CATALOG).map(([category, variables]) => (
                <div key={category} style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: brandIdentity.typography.sizes.medium,
                    fontWeight: brandIdentity.typography.weights.semibold,
                    color: brandIdentity.colors.primary,
                    borderBottom: `1px solid ${brandIdentity.colors.borderLight}`,
                    paddingBottom: '4px'
                  }}>
                    {category.split(' (')[0]}
                  </h4>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {variables.map((variable) => (
                      <div
                        key={variable.token}
                        onClick={() => insertVariableFromSidebar(variable.token)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: brandIdentity.colors.backgroundLight,
                          border: `1px solid ${brandIdentity.colors.borderLight}`,
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: brandIdentity.typography.sizes.small,
                          color: brandIdentity.colors.textDark,
                          transition: 'all 0.2s ease',
                          userSelect: 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = brandIdentity.colors.primary;
                          e.target.style.color = brandIdentity.colors.white;
                          e.target.style.transform = 'translateX(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = brandIdentity.colors.backgroundLight;
                          e.target.style.color = brandIdentity.colors.textDark;
                          e.target.style.transform = 'translateX(0)';
                        }}
                      >
                        {variable.label}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Styles */}
      <style jsx>{`
        .variable-chip {
          background-color: #F3F4F6 !important;
          border: 1px solid #CBD5E1 !important;
          border-radius: 12px !important;
          padding: 2px 8px !important;
          margin: 0 2px !important;
          font-size: 14px !important;
          font-family: monospace !important;
          color: #374151 !important;
          display: inline-block !important;
          cursor: pointer !important;
          user-select: none !important;
          transition: all 0.2s ease !important;
        }
        
        .variable-chip:hover {
          background-color: #E5E7EB !important;
          transform: scale(1.05) !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
        
        .suggestion-highlight {
          background-color: #FEF3C7 !important;
          border-radius: 3px !important;
        }
        
        .contract-editor {
          min-height: 400px;
          padding: 24px;
          border: 1px solid #D1D5DB;
          border-radius: 8px;
          background: white;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          line-height: 1.6;
          outline: none;
          position: relative;
        }
        
        .contract-editor:empty:before {
          content: 'Escribe aquí el contenido de tu contrato...';
          color: #9CA3AF;
          font-style: italic;
          pointer-events: none;
          position: absolute;
          top: 24px;
          left: 24px;
        }
        
        .contract-editor:focus:empty:before {
          opacity: 0.7;
        }
        
        .contract-editor:focus-within {
          border-color: ${brandIdentity.colors.primary};
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        .preview-mode {
          background-color: #F9FAFB !important;
          border-style: dashed !important;
        }
        
        .contract-editor p {
          margin: 8px 0;
        }
        
        .contract-editor h1, .contract-editor h2, .contract-editor h3 {
          margin: 16px 0 8px 0;
          font-weight: 600;
        }
        
        .contract-editor table {
          border-collapse: collapse;
          width: 100%;
          margin: 16px 0;
        }
        
        .contract-editor td, .contract-editor th {
          border: 1px solid #D1D5DB;
          padding: 8px;
          text-align: left;
        }
        
        .contract-editor th {
          background-color: #F9FAFB;
          font-weight: 600;
        }
        
        /* Page Break Styles */
        .contract-editor .page-break {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 16px;
          margin: 32px 0;
          border-top: 2px dashed #6366F1;
          border-bottom: 2px dashed #6366F1;
          background: #F0F9FF;
          color: #6366F1;
          font-weight: 600;
          font-size: 14px;
          border-radius: 4px;
          user-select: none;
          position: relative;
        }
        
        .contract-editor .page-break:hover {
          background: #DBEAFE;
        }
        
        /* Print styles for page breaks */
        @media print {
          .contract-editor .page-break {
            page-break-before: always;
            display: none !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            background: none !important;
          }
        }
        
        .preview-mode .page-break {
          page-break-before: always;
          display: block !important;
          height: 2px;
          background: linear-gradient(90deg, #6366F1 50%, transparent 50%);
          background-size: 20px 2px;
          border: none;
          margin: 20px 0;
          position: relative;
        }
        
        .preview-mode .page-break::before {
          content: 'Nueva página';
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          padding: 0 8px;
          font-size: 12px;
          color: #6366F1;
          font-weight: normal;
        }
        
        .toolbar-btn {
          padding: 6px 10px;
          border: 1px solid #D1D5DB;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          margin-right: 4px;
          transition: all 0.2s ease;
        }
        
        .toolbar-btn:hover {
          background-color: #F3F4F6;
        }
        
        .toolbar-btn.active {
          background-color: ${brandIdentity.colors.primary};
          color: white;
          border-color: ${brandIdentity.colors.primary};
        }

        .toolbar-btn.table-control {
          animation: slideIn 0.2s ease-in-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .editor-toolbar {
          display: flex;
          align-items: center;
          padding: 12px;
          background-color: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 8px 8px 0 0;
          flex-wrap: wrap;
          gap: 4px;
        }
      `}</style>
    </div>
  );
};

export default ContractEditor;